import { useCallback, useEffect, useRef, useState } from 'react';
import { AppState, StatusBar, StyleSheet, Text, View } from 'react-native';
import { Camera, useCameraDevice, useCameraPermission } from 'react-native-vision-camera';
import { Screen } from '~/components/Screen';
import { Notice, OutlineButton, PrimaryButton } from '~/components/ui';
import { logger } from '~/lib/logger';
import { useTheme } from '~/theme/ThemeProvider';

/**
 * Native photo capture for a single glass area or extra shot.
 *
 * Web uses a file input (`<input type="file" capture="environment">`) which hands
 * the OS camera app the job. Native has no such affordance, so this is the
 * in-app shutter — and it is the same VisionCamera lifecycle discipline as the
 * VIN scanner: `isActive` follows app foreground and an `active` prop, because
 * the camera is an exclusive system resource and holding it starves every other
 * app of it.
 *
 * Drawn on the inverse surface, like Shoot and the VIN scanner. Every camera in this app
 * is dark, because a white chrome around a viewfinder washes the frame out.
 */

export interface PhotoCaptureScreenProps {
  /** Human label for what is being photographed, e.g. "front windshield". */
  label: string;
  /** Guidance line under the label. */
  hint?: string;
  onCapture: (uri: string) => void;
  onCancel: () => void;
  /** False while this overlay is not the visible one — keeps the camera closed. */
  active?: boolean;
}

export function PhotoCaptureScreen({
  label,
  hint,
  onCapture,
  onCancel,
  active = true,
}: PhotoCaptureScreenProps) {
  const { c, radius, space, t } = useTheme();
  const cameraRef = useRef<Camera>(null);
  const { hasPermission, requestPermission } = useCameraPermission();
  const device = useCameraDevice('back');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Permissive initial read; see the note in VinScanScreen — currentState is
  // null or 'unknown' early in an Android cold launch.
  const [appActive, setAppActive] = useState(
    AppState.currentState !== 'background' && AppState.currentState !== 'inactive',
  );

  useEffect(() => {
    const sub = AppState.addEventListener('change', (next) => setAppActive(next === 'active'));
    return () => sub.remove();
  }, []);

  useEffect(() => {
    if (!hasPermission) void requestPermission();
  }, [hasPermission, requestPermission]);

  // Deliberately does NOT include `busy`. Adding it deactivates the camera on
  // the very render that starts a capture, so takePhoto then fires against a
  // closing session and fails with "Camera is closed." Double-taps are already
  // prevented by the disabled shutter and the busy guard inside shoot().
  const cameraActive = hasPermission && appActive && active;

  const shoot = useCallback(async () => {
    logger.log('[PhotoCapture] shutter', { hasCamera: !!cameraRef.current, busy, label });
    if (!cameraRef.current || busy) return;
    setBusy(true);
    setError(null);
    try {
      const photo = await cameraRef.current.takePhoto({});
      const uri = /^(blob:|data:|file:)/.test(photo.path) ? photo.path : `file://${photo.path}`;
      logger.log('[PhotoCapture] captured', uri.slice(0, 60));
      onCapture(uri);
    } catch (e) {
      logger.error('[PhotoCapture] failed', e);
      setError('Could not take that photo. Try again.');
    } finally {
      setBusy(false);
    }
  }, [busy, label, onCapture]);

  return (
    <Screen style={{ backgroundColor: c.inverse }}>
      {/* The app is light; this screen is not. */}
      <StatusBar barStyle="light-content" />
      <View style={{ flex: 1, padding: space.md, gap: space.md }}>
        <View>
          <Text style={[t.h2, { color: c.onInverse }]}>{label}</Text>
          {hint ? <Text style={[t.body, { color: c.onInverseMuted }]}>{hint}</Text> : null}
        </View>

        <View
          style={{
            flex: 1,
            borderRadius: radius.lg,
            overflow: 'hidden',
            backgroundColor: c.inverseRaise,
            justifyContent: 'center',
          }}
        >
          {hasPermission && device ? (
            <Camera
              ref={cameraRef}
              style={StyleSheet.absoluteFill}
              device={device}
              isActive={cameraActive}
              photo
              onError={(e) => {
                logger.error('[PhotoCapture] camera error', e.code, e.message);
                setError(
                  e.code === 'device/camera-already-in-use'
                    ? 'Another app is using the camera. Close it and try again.'
                    : `Camera problem (${e.code}).`,
                );
              }}
            />
          ) : (
            <View style={{ alignItems: 'center', padding: space.lg, gap: space.md }}>
              <Text style={[t.body, { color: c.onInverse, textAlign: 'center' }]}>
                {hasPermission ? 'No camera found.' : 'Camera access is needed to take photos.'}
              </Text>
              {!hasPermission ? (
                <PrimaryButton
                  label="Allow camera"
                  onPress={() => void requestPermission()}
                  onDark
                />
              ) : null}
            </View>
          )}
          {/* Solid, not a 45 % line: a translucent frame vanishes over bright glass. */}
          <View
            pointerEvents="none"
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              margin: space.xl,
              borderWidth: 2,
              borderColor: c.onInverseMuted,
              borderRadius: radius.md,
            }}
          />
        </View>

        {error ? (
          <Notice tone="stateFailed" title="Could not take that photo.">
            Try again.
          </Notice>
        ) : null}

        <PrimaryButton
          testID="photo-shutter"
          label="Take photo"
          busy={busy}
          disabled={!hasPermission}
          onPress={() => void shoot()}
          onDark
        />
        <OutlineButton testID="photo-cancel" label="Cancel" onPress={onCancel} onDark />
      </View>
    </Screen>
  );
}
